import { RefObject, useCallback, useLayoutEffect, useState } from "react";

/**
 * Placement for a portalled dropdown that is aware of the room it has.
 *
 * The panel this replaced was pinned to `rect.bottom` and anchored by its RIGHT
 * edge, with no height cap and nothing scrollable inside. Three consequences,
 * all reported from the app: on a narrow card the panel ran off the LEFT edge
 * of the viewport with no way to reach it; on a short viewport its last rows sat
 * below the fold, unreachable because the element is `fixed` so page scroll does
 * not move it; and it never followed its trigger once the page scrolled.
 *
 * This measures the panel and the space around it, then:
 *   · opens downward when there is room, and flips up when there is more room up
 *   · caps its height to whatever that direction actually offers, so the panel
 *     scrolls internally rather than overflowing off-screen
 *   · clamps horizontally into the viewport instead of trusting the trigger
 *   · recomputes on scroll and resize so it stays attached
 */

const GAP = 8;
const MARGIN = 12;
/** Never squeeze below this — better to overlay the trigger than show two rows. */
const MIN_PANEL_HEIGHT = 180;

export interface Placement {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  direction: "up" | "down";
  /** False until the first measure lands, so the panel is not painted mid-flight. */
  ready: boolean;
}

const HIDDEN: Placement = {
  top: 0,
  left: 0,
  width: 0,
  maxHeight: 0,
  direction: "down",
  ready: false,
};

const same = (a: Placement, b: Placement) =>
  a.ready === b.ready &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.maxHeight === b.maxHeight &&
  a.direction === b.direction;

export const useMenuPlacement = (
  open: boolean,
  triggerRef: RefObject<HTMLElement>,
  panelRef: RefObject<HTMLElement>,
  preferredWidth: number,
): Placement => {
  const [placement, setPlacement] = useState<Placement>(HIDDEN);

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const width = Math.min(preferredWidth, vw - MARGIN * 2);

    // The panel's natural height, read while it is unconstrained.
    const natural = panel.scrollHeight;
    const roomBelow = vh - rect.bottom - GAP - MARGIN;
    const roomAbove = rect.top - GAP - MARGIN;

    // Down unless up genuinely offers more AND down cannot hold the panel.
    const down = natural <= roomBelow || roomBelow >= roomAbove;
    const room = Math.max(MIN_PANEL_HEIGHT, down ? roomBelow : roomAbove);
    const maxHeight = Math.min(natural, room);

    const top = down ? rect.bottom + GAP : Math.max(MARGIN, rect.top - GAP - maxHeight);

    // Right edges aligned, as drawn — then clamped, which is the part the old
    // one skipped. `left` rather than `right` so the clamp is expressible.
    const left = Math.min(Math.max(MARGIN, rect.right - width), vw - width - MARGIN);

    const next: Placement = {
      top,
      left,
      width,
      maxHeight,
      direction: down ? "down" : "up",
      ready: true,
    };
    // Settle on an identical measurement instead of re-rendering. The
    // ResizeObserver below watches the very element whose height this sets, so
    // an unguarded setState would bounce measure → resize → measure.
    setPlacement((prev) => (same(prev, next) ? prev : next));
  }, [triggerRef, panelRef, preferredWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(HIDDEN);
      return undefined;
    }
    measure();

    // Capture phase, so a scrolling ancestor that stops propagation cannot
    // strand the panel away from its trigger.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && panelRef.current) {
      // Content can change height after mount (a row wraps, a font lands).
      ro = new ResizeObserver(measure);
      ro.observe(panelRef.current);
    }

    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [open, measure, panelRef]);

  return placement;
};
